import { useEffect, useMemo, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MapboxOverlay } from "@deck.gl/mapbox";
import { PathLayer, PolygonLayer, ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";

import { useStore } from "../state/store";
import type { Box, Storm } from "../engine/types";
import type { Leg } from "../engine/compute";
import { windRadiiPolygon, sectorPolygon } from "./windField";
import { loadCoastline, type CoastlineDense } from "../data/replayApi";
import type { FootLayer } from "../state/store";
import type { AnalogResult } from "../engine/analog";
import { ensembleSets } from "../engine/liveGuidance";
import { genesisColor, nhcAreaColor } from "../features/GenesisWatchPanel";
import { ensembleColor } from "../data/liveApi";
import type { GenesisWatch, GtwoFeature, StormRef } from "../data/liveApi";
import { inRing, systemName } from "../engine/liveSystems";
import { setMapHandle } from "./mapHandle";
import { schedulePermalinkWrite } from "../state/permalink";
import { lonNear, unwrapPath, unwrapPolygon, unwrappedBounds } from "../engine/geo";
import { sheetHeight } from "../state/layout";
import { addReferenceLines, REF_BELOW, showReferenceLines } from "./referenceLines";
import { cityFeatures, showCities, type CityValues } from "./futureCities";
import { lastMidnightUtc, markTimes, positionAt, utcMs } from "../engine/modelTimes";
import { FrameEngine, type EngineStatus } from "./overlays/frameEngine";
import { registerEnhancedIr } from "./overlays/enhancedIr";
import { useOverlayFrames, type ViewState } from "./overlays/useOverlayFrames";
import { surgeFrameSet, type FutureInfo } from "./overlays/providers";
import OverlayControl from "./overlays/OverlayControl";
import {
  buildSystemLayers, buildSystemPins, declutterLabels, systemTooltip, type LabelSpot, type SystemPin,
} from "./systemLayers";

// radar/satellite loops pull many tiles at once from several hosts; the
// default budget (16) serialises a multi-frame loop's first load
maplibregl.setMaxParallelImageRequests(32);
registerEnhancedIr();

// the live overview: centred on the US, the Central Pacific (Hawaii) to the
// Cape Verde islands, so both hurricane basins are in view at once
export const OVERVIEW_BOUNDS: [[number, number], [number, number]] = [[-168, 4], [-15, 50]];
const OVERVIEW_PADDING = 24;
// a phone is too narrow for both basins: it opens on the Gulf, the Caribbean
// and the East Pacific off Mexico (the sheet lists every system)
const OVERVIEW_BOUNDS_PHONE: [[number, number], [number, number]] = [[-118, 8], [-58, 38]];
const overviewBounds = () => (useStore.getState().ui.layout === "phone" ? OVERVIEW_BOUNDS_PHONE : OVERVIEW_BOUNDS);

// ── antimeridian guard, applied ONCE to every layer handed to deck.gl ──
// deck draws a segment from −179° to +179° around the whole world (Lowell,
// EP12 2026: recurving members crossing 180° painted horizontal lines
// circling the earth). Cloning each layer with unwrapped path/polygon/point
// accessors — anchored to the viewer's current centre longitude — fixes it
// for every builder (spaghetti, ensembles, tracks, cones, footprints,
// climatology, analogs) without touching them individually.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function datelineSafe(layer: any, refLon: number) {
  const p = layer?.props ?? {};
  const patch: Record<string, unknown> = {};
  if (typeof p.getPath === "function") {
    const g = p.getPath;
    patch.getPath = (d: unknown, info: unknown) => unwrapPath(g(d, info), refLon);
  }
  if (typeof p.getPolygon === "function") {
    const g = p.getPolygon;
    patch.getPolygon = (d: unknown, info: unknown) => unwrapPolygon(g(d, info), refLon);
  }
  if (typeof p.getPosition === "function") {
    const g = p.getPosition;
    patch.getPosition = (d: unknown, info: unknown) => {
      const pos = g(d, info);
      return Array.isArray(pos) && pos.length >= 2 ? [lonNear(pos[0], refLon), ...pos.slice(1)] : pos;
    };
  }
  return Object.keys(patch).length ? layer.clone(patch) : layer;
}

const BASEMAP = "https://basemaps.cartocdn.com/gl/dark-matter-gl-style/style.json";

const LEG_COLOR: Record<Leg, [number, number, number, number]> = {
  a: [96, 165, 250, 55], // Box-A-only tracks kept faint to cut clutter
  ab: [244, 114, 182, 190],
  abc: [167, 139, 250, 230],
};
const BOX_COLOR = {
  A: [96, 165, 250] as [number, number, number],
  B: [244, 114, 182] as [number, number, number],
  C: [167, 139, 250] as [number, number, number],
};

function boxRing(b: Box): [number, number][] {
  return [
    [b.lon_min, b.lat_min],
    [b.lon_max, b.lat_min],
    [b.lon_max, b.lat_max],
    [b.lon_min, b.lat_max],
  ];
}

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
}

function viewOf(map: maplibregl.Map): ViewState {
  const c = map.getCenter();
  const b = map.getBounds();
  return { lng: c.lng, lat: c.lat, bbox: [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()] };
}

interface Props {
  legByStormId: Map<string, Leg>;
  analog: AnalogResult | null;
}

export default function Canvas({ legByStormId, analog }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const overlayRef = useRef<MapboxOverlay | null>(null);
  const transientRef = useRef<Box | null>(null);
  const drawStartRef = useRef<[number, number] | null>(null);

  const [drawMode, setDrawMode] = useState(false);
  const drawModeRef = useRef(drawMode);
  drawModeRef.current = drawMode;
  const [mapLoaded, setMapLoaded] = useState(false);

  // Dense coastline for the surge ribbon (loaded once, cached).
  const [coastline, setCoastline] = useState<CoastlineDense | null>(null);
  useEffect(() => {
    loadCoastline().then(setCoastline).catch(() => {});
  }, []);

  const mode = useStore((s) => s.mode);
  const tracks = useStore((s) => s.tracks);
  const boxA = useStore((s) => s.boxA);
  const boxB = useStore((s) => s.boxB);
  const boxC = useStore((s) => s.boxC);
  const addBox = useStore((s) => s.addBox);
  const bundle = useStore((s) => s.live.bundle);
  const shownEnsembles = useStore((s) => s.live.shownEnsembles);
  const ensembleAlpha = useStore((s) => s.live.ensembleAlpha);
  const liveModels = useStore((s) => s.live.models);
  const genesis = useStore((s) => s.live.genesis);
  const selectedGenesis = useStore((s) => s.live.selectedGenesis);
  const showNhcOutlook = useStore((s) => s.live.showNhcOutlook);
  const showGenesisDots = useStore((s) => s.live.showGenesisDots);
  const liveIndex = useStore((s) => s.live.index);
  const liveIndexKind = useStore((s) => s.live.indexKind);
  const selectedLiveId = useStore((s) => s.live.selectedId);
  const runScrubActive = useStore((s) => s.live.runScrubActive);
  const scrubBundle = useStore((s) => s.live.scrubBundle);
  const scrubPrevBundle = useStore((s) => s.live.scrubPrevBundle);
  const officialGis = useStore((s) => s.live.officialGis);
  const showOfficial = useStore((s) => s.live.showOfficial);
  const alerts = useStore((s) => s.live.alerts);
  const showAlerts = useStore((s) => s.live.showAlerts);
  const recon = useStore((s) => s.live.recon);
  const showRecon = useStore((s) => s.live.showRecon);
  const overlays = useStore((s) => s.overlays);
  const setSatellite = useStore((s) => s.setSatellite);
  const toggleRadar = useStore((s) => s.toggleRadar);
  const setOverlayOpacity = useStore((s) => s.setOverlayOpacity);
  const toggleMotion = useStore((s) => s.toggleMotion);
  const setOverlaySpeed = useStore((s) => s.setOverlaySpeed);
  const setLoopMin = useStore((s) => s.setLoopMin);
  const setOcean = useStore((s) => s.setOcean);
  const toggleLightning = useStore((s) => s.toggleLightning);
  const toggleMeso = useStore((s) => s.toggleMeso);
  const toggleFuture = useStore((s) => s.toggleFuture);
  const setFutureProduct = useStore((s) => s.setFutureProduct);
  const setFutureModel = useStore((s) => s.setFutureModel);
  const setFutureChanceIn = useStore((s) => s.setFutureChanceIn);
  const setFutureInfo = useStore((s) => s.setFutureInfo);
  const futureInfo = useStore((s) => s.live.futureInfo);
  const futureIdx = useStore((s) => s.live.futureIdx);
  const outlookArea = useStore((s) => s.live.outlookArea);
  const futureArea = useStore((s) => s.live.futureArea);
  const layout = useStore((s) => s.ui.layout);
  const surgeInfo = useStore((s) => s.live.surge);
  const showSurge = useStore((s) => s.live.showSurge);
  const compare = useStore((s) => s.compare);
  const climoOverlay = useStore((s) => s.climoOverlay);
  const replay = useStore((s) => s.explorer.replay);
  const exHour = useStore((s) => s.explorer.hour);
  const footLayers = useStore((s) => s.explorer.footLayers);
  const toggleFoot = useStore((s) => s.toggleFoot);
  const focusStormId = useStore((s) => s.focus.stormId);
  const focusSource = useStore((s) => s.focus.source);
  const focusReplay = useStore((s) => s.focus.replay);
  const focusTrack = useStore((s) => s.focus.track);
  const focusHour = useStore((s) => s.focus.hour);

  const tracksById = useMemo(() => {
    const m = new Map<string, Storm>();
    for (const s of tracks) m.set(s.storm_id, s);
    return m;
  }, [tracks]);

  // the camera after each move (overlay feeds follow it; storm labels
  // re-declutter against it)
  const [view, setView] = useState<ViewState | null>(null);

  // every active storm + invest as a clickable symbol (live feed only — the
  // off-season demo list has no current positions)
  const systems = useMemo(
    () => buildSystemPins(liveIndexKind === "active" ? liveIndex : [], genesis?.gtwo),
    [liveIndex, liveIndexKind, genesis],
  );
  // which of their labels fit at this view — re-placed after every move, but
  // keyed by content so the deck layers only rebuild when the set changes
  const systemLabelKey = useMemo(() => {
    const map = mapRef.current;
    if (!map || mode !== "live") return "";
    const center = map.getCenter().lng;
    const spots = declutterLabels(systems.pins.filter((p) => p.id !== selectedLiveId), (p) => {
      const pt = map.project([lonNear(p.lon, center), p.lat]);
      return [pt.x, pt.y];
    });
    return [...spots].map(([id, s]) => `${id}:${s}`).sort().join(",");
  }, [systems, selectedLiveId, view, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  const systemLabels = useMemo(
    () => new Map(systemLabelKey ? systemLabelKey.split(",").map((kv) => kv.split(":") as [string, LabelSpot]) : []),
    [systemLabelKey],
  );

  const updateLayers = useMemo(
    () => () => {
      const overlay = overlayRef.current;
      if (!overlay) return;

      const focusActive = !!focusStormId;
      const dim = focusActive ? 0.16 : 1; // dim the backdrop when a storm is focused

      // ── backdrop layers (per mode), dimmed under a focus ──
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let backdrop: any[];

      if (mode === "live") {
        const analogLayers = analog ? buildAnalogLayers(analog, dim) : [];
        // run-to-run scrub: render the selected past cycle in place of the
        // latest, with the prior cycle's OFCL ghosted to show the shift.
        const scrubbing = runScrubActive && !!scrubBundle;
        const activeBundle = scrubbing ? scrubBundle : bundle;
        backdrop = [
          // an invest's symbol carries its NHC odds — drop the duplicate ✕
          ...(showNhcOutlook ? buildGtwoLayers(genesis?.gtwo, systems.matched, outlookArea?.id ?? futureArea?.id ?? null) : []),
          ...buildGenesisLayers(genesis, selectedGenesis, showGenesisDots),
          ...analogLayers,
          ...(showAlerts ? buildAlertLayers(alerts) : []),
          ...(showOfficial ? buildOfficialLayers(officialGis, dim) : []),
          // a live storm's wind field (NHC's measured radii), under the tracks. The
          // simulated footprints (surge tiers, tornado sector, rain band) are for
          // replays: live, NHC's surge map and the futurecast's rain replace them
          ...(activeBundle?.threat && activeBundle.best_track.length
            ? buildFootprintLayers(
                activeBundle.best_track[activeBundle.best_track.length - 1],
                activeBundle.threat.foot, { ...footLayers, surge: false, tornado: false, rain: false }, coastline, "live")
            : []),
          ...(scrubbing ? buildGhostLayers(scrubPrevBundle, dim) : []),
          ...buildLiveLayers(activeBundle, dim, { models: liveModels, shownEnsembles, ensembleAlpha }),
          ...(overlays.future && futureInfo ? buildFutureTrackLayers(futureInfo, dim) : []),
          // aircraft observations on top: the freshest ground truth on the map
          ...(showRecon ? buildReconLayers(recon, dim) : []),
          // the way into every storm: its symbol, above everything else
          ...(scrubbing ? [] : buildSystemLayers(systems.pins, selectedLiveId, systemLabels)),
        ];
      } else if (mode === "explorer") {
        backdrop = buildExplorerLayers(replay, exHour, footLayers, coastline);
      } else {
        // climatology mode
        const paths: { path: [number, number][]; color: number[]; width: number; storm_id: string; name: string; year: number }[] = [];
        for (const [storm_id, leg] of legByStormId) {
          const storm = tracksById.get(storm_id);
          if (!storm) continue;
          paths.push({
            path: storm.track.map((p) => [p.lon, p.lat]),
            color: LEG_COLOR[leg],
            width: leg === "abc" ? 2.5 : leg === "ab" ? 2 : 1,
            storm_id, name: storm.name, year: storm.year,
          });
        }
        const boxes: { polygon: [number, number][]; color: [number, number, number] }[] = [];
        if (boxA) boxes.push({ polygon: boxRing(boxA), color: BOX_COLOR.A });
        if (boxB) boxes.push({ polygon: boxRing(boxB), color: BOX_COLOR.B });
        if (boxC) boxes.push({ polygon: boxRing(boxC), color: BOX_COLOR.C });
        const transient = transientRef.current;
        const liveOverlay = climoOverlay && bundle ? buildLiveLayers(bundle, dim, { models: liveModels, shownEnsembles, ensembleAlpha }) : [];

        backdrop = [
          ...liveOverlay,
          new PathLayer({
            id: "tracks", data: paths,
            getPath: (d) => d.path,
            getColor: (d) => d.color as [number, number, number, number],
            getWidth: (d) => d.width, widthUnits: "pixels", widthMinPixels: 1,
            jointRounded: true, capRounded: true, opacity: dim,
            pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 160],
          }),
          new PolygonLayer({
            id: "boxes", data: boxes,
            getPolygon: (d) => d.polygon,
            getFillColor: (d) => [...d.color, 35] as [number, number, number, number],
            getLineColor: (d) => [...d.color, 255] as [number, number, number, number],
            getLineWidth: 2, lineWidthUnits: "pixels", stroked: true, filled: true, opacity: dim,
          }),
          transient &&
            new PolygonLayer({
              id: "transient", data: [{ polygon: boxRing(transient) }],
              getPolygon: (d) => d.polygon,
              getFillColor: [255, 255, 255, 25], getLineColor: [255, 255, 255, 200],
              getLineWidth: 1.5, lineWidthUnits: "pixels", stroked: true, filled: true,
            }),
        ].filter(Boolean);
      }

      // ── focused storm drawn on top at full opacity ──
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let focusLayers: any[] = [];
      if (focusActive) {
        if (focusSource === "replay" && focusReplay) focusLayers = buildExplorerLayers(focusReplay, focusHour, footLayers, coastline);
        else if (focusSource === "track" && focusTrack) focusLayers = buildTrackLayers(focusTrack);
      }

      // compare (amber past storm) is mode-agnostic, like focus — it follows
      // you across climatology/explorer/live instead of vanishing on switch
      const refLon = mapRef.current?.getCenter().lng ?? -60;
      overlay.setProps({
        layers: [...backdrop, ...buildCompareLayers(compare), ...focusLayers].map((l) => datelineSafe(l, refLon)),
      });
    },
    [mode, bundle, analog, climoOverlay, shownEnsembles, ensembleAlpha, liveModels, genesis, selectedGenesis, showNhcOutlook,
     showGenesisDots, systems, selectedLiveId, systemLabels,
     officialGis, showOfficial, alerts, showAlerts, recon, showRecon,
     runScrubActive, scrubBundle, scrubPrevBundle, compare, replay, exHour, footLayers, coastline, legByStormId, tracksById, boxA, boxB, boxC,
     focusStormId, focusSource, focusReplay, focusTrack, focusHour, overlays.future, futureInfo, outlookArea, futureArea],
  );

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    setMapLoaded(false); // state survives HMR remounts — the NEW map isn't loaded yet
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: BASEMAP, bounds: overviewBounds(),
      fitBoundsOptions: {
        padding: useStore.getState().ui.layout === "phone" ? { top: 64, bottom: 136, left: 12, right: 12 } : OVERVIEW_PADDING,
      },
      attributionControl: { compact: true },
      // radar/satellite frames are immutable timestamped images; re-fetching
      // them when their short Cache-Control lapses was a periodic reload of
      // the entire loop (the basemap's vector tiles never change either)
      refreshExpiredTiles: false,
    });
    mapRef.current = map;
    setMapHandle(map); // camera access for the permalink serializer
    map.on("moveend", schedulePermalinkWrite); // pan/zoom lands in the share URL
    // radar/satellite feeds follow the view (US radar vs global; rapid GOES
    // sector vs full disk; which geostationary bird)
    const track = () => setView(viewOf(map));
    map.on("load", track);
    map.on("moveend", track);
    // dev-only handle for debugging/E2E (mirrors window.__store)
    if (import.meta.env.DEV) {
      (window as unknown as { __map?: maplibregl.Map }).__map = map;
    }
    const overlay = new MapboxOverlay({
      interleaved: true,
      layers: [],
      getTooltip: ({ object }: {
        object?: {
          name?: string; year?: number; storm_id?: string;
          surgeName?: string; surgeLabel?: string; surgeColor?: string;
          kind?: string; prob7?: number | null; prob2?: number | null; title?: string;
        } | null;
      }) => {
        const style = {
          background: "rgba(17,24,39,0.95)", color: "#fff",
          font: "600 12px Inter, sans-serif", padding: "4px 9px",
          borderRadius: "6px", border: "1px solid rgba(255,255,255,0.18)",
        };
        // an active storm / invest symbol
        if (object && (object as Partial<SystemPin>).sys) {
          return { html: systemTooltip(object as unknown as SystemPin), style: { ...style, padding: "6px 10px" } };
        }
        // hurricane-hunter observation / center fix
        const r = object as (Partial<ReconDot> & Partial<ReconFixPt>) | null;
        if (r && r.reconKind === "ob") {
          const bits = [
            r.fl_kt != null ? `flight-level ${r.fl_kt} kt${r.fl_dir != null ? ` from ${r.fl_dir}°` : ""}` : null,
            r.sfmr_kt != null ? `<b>SFMR surface ${r.sfmr_kt} kt</b>` : null,
            r.slp != null ? `extrap. SLP ${r.slp.toFixed(1)} mb` : null,
            r.t_c != null ? `${r.t_c.toFixed(1)} °C` : null,
          ].filter(Boolean);
          return { html: `<div>✈️ ${r.ac} · ${r.t?.slice(11, 19)}Z<br/>${bits.join("<br/>")}</div>`, style };
        }
        if (r && r.reconKind === "fix") {
          return {
            html: `<div>✈️ Center fix · ${r.t?.slice(5, 10)} ${r.t?.slice(11, 16)}Z${r.ac ? ` · ${r.ac}` : ""}<br/>` +
              `<b>${r.mslp ?? "—"} mb</b> ${r.extrap ? "(extrapolated)" : "(dropsonde)"}` +
              `${r.eye ? `<br/>Eye: ${r.eye}` : ""}` +
              `${r.max_fl_kt != null ? `<br/>Max flight-level wind ${r.max_fl_kt} kt` : ""}` +
              `${r.max_sfc_kt != null ? `<br/>Max SFMR surface wind ${r.max_sfc_kt} kt` : ""}</div>`,
            style,
          };
        }
        // model guidance line hover → which model is this? (a time dot: and where, when)
        const o = object as { techName?: string; tech?: string; event?: string; headline?: string; label?: string; code?: string;
          dotTime?: number; vmax?: number | null } | null;
        if (o && o.techName && o.dotTime) {
          const when = new Date(o.dotTime).toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "numeric", timeZoneName: "short" });
          return { html: `<div>📡 ${o.techName} · ${when}${o.vmax ? ` · ${o.vmax} kt` : ""}</div>`, style };
        }
        if (o && o.techName) {
          return { html: `<div>📡 ${o.techName}${o.tech && o.tech !== o.techName ? ` (${o.tech})` : ""}</div>`, style };
        }
        // NWS alert polygon hover
        if (o && o.event && o.headline !== undefined) {
          return { html: `<div>⚠️ ${o.event}</div>`, style };
        }
        // official watch/warning segment hover
        if (o && o.code !== undefined && o.label) {
          return { html: `<div>🏛️ NHC ${o.label}</div>`, style };
        }
        // NHC GTWO area/marker hover → official formation chance (a click opens it, but
        // in the rain view a tap is the rain total there)
        if (object && (object.kind === "area" || object.kind === "point")) {
          const p7 = object.prob7 == null ? "?" : `${object.prob7}%`;
          const p2 = object.prob2 == null ? null : `${object.prob2}%`;
          const st = useStore.getState();
          const rainTap = st.overlays.future && (st.overlays.futureProduct === "rain" || st.overlays.futureProduct === "chance")
            && !!st.live.futureInfo;
          return {
            html: `<div>🇺🇸 NHC formation chance${object.title ? ` · ${esc(object.title)}` : ""}<br/>` +
              `7-day: <b>${p7}</b>${p2 ? ` · 48h: <b>${p2}</b>` : ""}` +
              `${rainTap ? "" : '<br/><span style="color:#60A5FA">Click for what the models do with it ›</span>'}</div>`,
            style,
          };
        }
        // surge band hover → "<coast> — <Tier> surge"
        if (object && object.surgeName) {
          return {
            html: `<div>🌊 ${object.surgeName} — <b style="color:${object.surgeColor ?? "#fff"}">${object.surgeLabel ?? ""}</b> surge</div>`,
            style,
          };
        }
        if (object && object.storm_id && object.name) {
          const nm =
            object.name === "UNNAMED"
              ? "Unnamed"
              : object.name.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
          return { html: `<div>🌀 ${nm} (${object.year})</div>`, style };
        }
        return null;
      },
      // click any storm-bearing line (analog path or climatology track) → focus it
      onClick: ({ object }: { object?: { storm_id?: string } | null }) => {
        if (object && object.storm_id) void useStore.getState().focusStorm(object.storm_id);
      },
      // pointer cursor over clickable tracks so they read as interactive
      getCursor: ({ isHovering }: { isHovering: boolean }) => (isHovering ? "pointer" : "grab"),
    });
    map.addControl(overlay as unknown as maplibregl.IControl);
    overlayRef.current = overlay;

    map.on("load", () => { addGraticule(map); addReferenceLines(map); setMapLoaded(true); });

    map.on("mousedown", (e) => {
      if (!drawModeRef.current) return;
      drawStartRef.current = [e.lngLat.lng, e.lngLat.lat];
    });
    map.on("mousemove", (e) => {
      if (!drawModeRef.current || !drawStartRef.current) return;
      const [lng1, lat1] = drawStartRef.current;
      transientRef.current = {
        lat_min: Math.min(lat1, e.lngLat.lat), lat_max: Math.max(lat1, e.lngLat.lat),
        lon_min: Math.min(lng1, e.lngLat.lng), lon_max: Math.max(lng1, e.lngLat.lng),
      };
      updateLayersRef.current();
    });
    map.on("mouseup", (e) => {
      if (!drawModeRef.current || !drawStartRef.current) return;
      const [lng1, lat1] = drawStartRef.current;
      const r2 = (n: number) => Math.round(n * 100) / 100;
      const box: Box = {
        lat_min: r2(Math.min(lat1, e.lngLat.lat)), lat_max: r2(Math.max(lat1, e.lngLat.lat)),
        lon_min: r2(Math.min(lng1, e.lngLat.lng)), lon_max: r2(Math.max(lng1, e.lngLat.lng)),
      };
      drawStartRef.current = null;
      transientRef.current = null;
      if (box.lat_max - box.lat_min > 0.05 && box.lon_max - box.lon_min > 0.05) addBox(box);
      setDrawMode(false);
      map.dragPan.enable();
      updateLayersRef.current();
    });

    return () => {
      setMapHandle(null);
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
    };
  }, [addBox]);

  const updateLayersRef = useRef(updateLayers);
  updateLayersRef.current = updateLayers;

  // two-finger gestures on a phone or tablet pan and zoom; they don't rotate or tilt the map
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (layout === "desktop") {
      map.touchZoomRotate.enableRotation();
      map.touchPitch.enable();
    } else {
      map.touchZoomRotate.disableRotation();
      map.touchPitch.disable();
    }
  }, [layout, mapLoaded]);

  // fly to a genesis disturbance when its row is selected — and RESTORE the
  // camera on deselect (this was the one fly-away with no way back)
  const preGenesisCamRef = useRef<{ center: maplibregl.LngLat; zoom: number } | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (selectedGenesis != null) {
      if (preGenesisCamRef.current === null) {
        preGenesisCamRef.current = { center: map.getCenter(), zoom: map.getZoom() };
      }
      const w = genesis?.watch[selectedGenesis];
      if (w) map.flyTo({ center: [w.lon, w.lat], zoom: Math.max(map.getZoom(), 4.5), duration: 800 });
    } else if (preGenesisCamRef.current) {
      map.easeTo({ center: preGenesisCamRef.current.center, zoom: preGenesisCamRef.current.zoom, duration: 600 });
      preGenesisCamRef.current = null;
    }
  }, [selectedGenesis, genesis]);

  useEffect(() => { updateLayers(); }, [updateLayers]);

  // ── raster overlays: satellite + radar, static or looping (map/overlays/) ──
  // Native MapLibre raster layers under the labels (the deck data layers stay
  // on top). Feeds follow the view; the FrameEngine owns layers + the clock.
  // While a replay is on screen, radar switches to the NEXRAD archive at the
  // replay's hour — Katrina's eyewall at landfall, not today's weather.
  const replayFrame =
    focusSource === "replay" && focusReplay ? focusReplay.frames[focusHour]
      : mode === "explorer" && !focusStormId && replay ? replay.frames[exHour]
        : null;
  const replayTime = replayFrame?.dt ? Date.parse(/[zZ]$/.test(replayFrame.dt) ? replayFrame.dt : `${replayFrame.dt}Z`) : null;
  // what the futurecast plays: the open storm, else an NHC outlook area
  const futureSubject = mode === "live" ? selectedLiveId ?? futureArea?.id ?? null : null;
  // a past storm replaying (Explorer, or one opened from any mode)
  const replaying = (mode === "explorer" && !!replay) || (focusSource === "replay" && !!focusReplay);
  // the live storm's latest fix anchors the 1-minute mesoscale loop
  const lastFix = mode === "live" && bundle?.best_track.length ? bundle.best_track[bundle.best_track.length - 1] : null;
  const stormPos = useMemo(
    () => (lastFix ? { lat: lastFix.lat, lon: lastFix.lon } : null),
    [lastFix?.lat, lastFix?.lon], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const {
    radar: radarSet, sat: satSet, satBase: satBaseSet, ocean: oceanSet, lightning: lightningSet, meso: mesoSet, mesoNote,
    future: futureData, futureNote,
  } = useOverlayFrames(overlays, view, Number.isFinite(replayTime) ? replayTime : null, stormPos, futureSubject);
  // NHC's official surge-flooding map rides the same raster stack (above radar)
  const surgeSet = useMemo(
    () => (mode === "live" && showSurge ? surgeFrameSet(surgeInfo) : null),
    [mode, showSurge, surgeInfo],
  );
  const engineRef = useRef<FrameEngine | null>(null);
  const [engineStatus, setEngineStatus] = useState<EngineStatus | null>(null);

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded || !map.getStyle()) return; // HMR-remount guard
    // imagery goes under the coastlines and borders drawn over it (referenceLines), and the labels
    const engine = new FrameEngine(map, () => (map.getLayer(REF_BELOW) ? REF_BELOW : map.getStyle()?.layers?.find((l) => l.type === "symbol")?.id));
    engineRef.current = engine;
    if (import.meta.env.DEV) (window as unknown as { __overlays?: FrameEngine }).__overlays = engine;
    const unsub = engine.subscribe(setEngineStatus);
    return () => { unsub(); engine.destroy(); engineRef.current = null; };
  }, [mapLoaded]);

  const archiveRadar = replayTime != null && overlays.radar;
  useEffect(() => { engineRef.current?.setMotion(overlays.motion && !archiveRadar); }, [overlays.motion, archiveRadar, mapLoaded]);
  useEffect(() => {
    engineRef.current?.setFrameSets({
      ocean: oceanSet, satbase: satBaseSet, sat: satSet, meso: mesoSet, radar: radarSet, surge: surgeSet,
      lightning: lightningSet, future: futureData?.set ?? null,
      // the impact zone's sharp picture (a phone keeps just the whole region: memory)
      futuredetail: layout === "phone" ? null : futureData?.detail ?? null,
    });
  }, [oceanSet, satBaseSet, satSet, mesoSet, radarSet, surgeSet, lightningSet, futureData, layout, mapLoaded]);

  // ── futurecast: the TimeDock steps it; the live imagery dims to a backdrop ──
  useEffect(() => { setFutureInfo(futureData?.info ?? null); }, [futureData, setFutureInfo]);
  useEffect(() => { engineRef.current?.setFutureIndex(futureIdx); }, [futureIdx, futureData, mapLoaded]);
  useEffect(() => { engineRef.current?.setLiveDim(futureData ? 0.5 : 1); }, [futureData, mapLoaded]);
  const futureStep = futureData && futureInfo ? futureInfo.steps[Math.min(futureIdx, futureInfo.steps.length - 1)] : null;
  // frame the storm's path once per storm (or outlook area); switching models
  // keeps the camera unless the new model's area is mostly off screen (HRRR's
  // US domain, say)
  const lastFutureFitRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !futureInfo || !futureData) { if (!futureData) lastFutureFitRef.current = null; return; }
    const id = `${futureSubject}|${futureInfo.source}:${futureInfo.cycle}`;
    if (lastFutureFitRef.current === id) return;
    const sameStorm = lastFutureFitRef.current?.startsWith(`${futureSubject}|`) ?? false;
    lastFutureFitRef.current = id;
    const [w, s, e, n] = futureInfo.focus;
    if (sameStorm) {
      const v = map.getBounds();
      const ow = Math.max(0, Math.min(e, v.getEast()) - Math.max(w, v.getWest()));
      const oh = Math.max(0, Math.min(n, v.getNorth()) - Math.max(s, v.getSouth()));
      if (ow * oh >= 0.6 * Math.max(1e-6, (e - w) * (n - s))) return;
    }
    const bounds = futureInfo.track.length >= 2 ? unwrappedBounds(futureInfo.track) : null;
    map.fitBounds(bounds ?? [[w, s], [e, n]], {
      padding: fitPad(map, { top: 120, bottom: 110, left: 330, right: 190 }, { top: 36, bottom: 96 }), maxZoom: 6, duration: 900,
    });
  }, [futureInfo, futureData, futureSubject]);
  // a marker rides the model's track with the step on screen
  const futureMarkerRef = useRef<maplibregl.Marker | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !futureStep || futureStep.lat == null || futureStep.lon == null || !futureInfo) {
      futureMarkerRef.current?.remove();
      futureMarkerRef.current = null;
      return;
    }
    if (!futureMarkerRef.current) {
      const el = document.createElement("div");
      el.className = "future-marker";
      futureMarkerRef.current = new maplibregl.Marker({ element: el, anchor: "left", offset: [-9, 0] })
        .setLngLat([futureStep.lon, futureStep.lat]).addTo(map);
    }
    futureMarkerRef.current.setLngLat([futureStep.lon, futureStep.lat]);
    futureMarkerRef.current.getElement().classList.toggle("remnant", futureStep.remnant);
    // the moment itself (local time), not "+84 h" from a run start nobody knows;
    // then the model's storm: wind, central pressure
    const at = futureStepWhen(futureStep.time);
    const storm = futureStep.remnant ? " · remnants"
      : `${futureStep.vmax ? ` · ${futureStep.vmax} kt` : ""}${futureStep.pmin ? ` · ${futureStep.pmin} mb` : ""}`;
    futureMarkerRef.current.getElement().innerHTML =
      `<span class="dot"></span><span class="lbl">${futureInfo.label} · ${at}${storm}</span>`;
    flipFutureLabel();
  }, [futureStep, futureInfo]); // eslint-disable-line react-hooks/exhaustive-deps
  // near the map's right edge the label goes on the dot's left (it would run under the panel)
  const flipFutureLabel = () => {
    const map = mapRef.current, m = futureMarkerRef.current;
    if (!map || !m) return;
    const lbl = m.getElement().querySelector(".lbl") as HTMLElement | null;
    const room = map.getContainer().clientWidth - map.project(m.getLngLat()).x;
    m.getElement().classList.toggle("flip", room < (lbl?.offsetWidth ?? 220) + 40);
  };
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    map.on("move", flipFutureLabel);
    return () => { map.off("move", flipFutureLabel); };
  }, [mapLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── the rain views: each town's total (or chance) at the step on screen ──
  const rainView = !!futureData && !!futureInfo && (futureInfo.product === "rain" || futureInfo.product === "chance");
  const citiesUrl = rainView ? futureInfo!.citiesUrl : null;
  const [cityData, setCityData] = useState<GeoJSON.FeatureCollection | null>(null);
  useEffect(() => {
    setCityData(null);
    if (!citiesUrl || !futureInfo) return;
    const info = futureInfo;
    const chance = info.product === "chance";
    // every step that can have a total (rain: those after "now")
    const want = info.steps.filter((s) => chance || s.fhr > info.fromFhr).length;
    let cancelled = false;
    let timer = 0;
    const load = async () => {
      try {
        const d = (await (await fetch(citiesUrl)).json()) as CityValues;
        if (cancelled) return;
        setCityData(cityFeatures(d, chance));
        if (d.steps.length < want) timer = window.setTimeout(load, 30_000); // frames still rendering: their towns follow
      } catch {
        if (!cancelled) timer = window.setTimeout(load, 60_000);
      }
    };
    void load();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [citiesUrl]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapLoaded) return;
    showCities(map, citiesUrl ? cityData : null, citiesUrl && futureStep ? futureStep.fhr : null);
  }, [cityData, citiesUrl, futureStep, mapLoaded]);

  // ── rain view: tap anywhere for the total at that spot (from now → the step on screen) ──
  const [rainPoint, setRainPoint] = useState<{ lng: number; lat: number; series: PointSeries | null } | null>(null);
  const rainPopupRef = useRef<maplibregl.Popup | null>(null);
  const rainOn = rainView;
  const rainOnRef = useRef(rainOn);
  rainOnRef.current = rainOn;
  const pointUrlRef = useRef<string | null>(null);
  pointUrlRef.current = futureInfo?.pointUrl ?? null;
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const onClick = (e: maplibregl.MapMouseEvent) => {
      if (!rainOnRef.current || !pointUrlRef.current || drawModeRef.current) return;
      // a storm symbol (opens that storm) or a storm track (focuses it) keeps its own click;
      // model lines, cones and the rest don't swallow the rain readout
      const hit = overlayRef.current?.pickObject({ x: e.point.x, y: e.point.y, radius: 4 })?.object as
        { sys?: boolean; storm_id?: string } | undefined;
      if (hit?.sys || hit?.storm_id) return;
      const { lng, lat } = e.lngLat;
      setRainPoint({ lng, lat, series: null });
      fetch(`${pointUrlRef.current}&lat=${lat.toFixed(3)}&lon=${lng.toFixed(3)}`)
        .then((r) => r.json())
        .then((d: { series?: PointSeries }) =>
          setRainPoint((cur) => (cur && cur.lng === lng && cur.lat === lat ? { ...cur, series: d.series ?? [] } : cur)))
        .catch(() => setRainPoint((cur) => (cur && cur.lng === lng ? { ...cur, series: [] } : cur)));
    };
    map.on("click", onClick);
    return () => { map.off("click", onClick); };
  }, [mapLoaded]);
  useEffect(() => { if (!rainOn) setRainPoint(null); }, [rainOn]);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !rainPoint || !futureInfo || !futureStep) {
      rainPopupRef.current?.remove();
      rainPopupRef.current = null;
      return;
    }
    if (!rainPopupRef.current) {
      rainPopupRef.current = new maplibregl.Popup({ closeButton: true, closeOnClick: false, className: "rain-popup", maxWidth: "270px" })
        .setLngLat([rainPoint.lng, rainPoint.lat]).addTo(map);
      rainPopupRef.current.on("close", () => { rainPopupRef.current = null; setRainPoint(null); });
    }
    rainPopupRef.current.setLngLat([rainPoint.lng, rainPoint.lat]).setHTML(futureInfo.product === "chance"
      ? chancePopupHtml(rainPoint.series, futureInfo, futureStep) : rainPopupHtml(rainPoint.series, futureInfo, futureStep));
  }, [rainPoint, futureInfo, futureStep]);
  // ── an NHC outlook area, clicked: frame it, and offer what the models do with it ──
  const areaPopupRef = useRef<{ popup: maplibregl.Popup; onClose: () => void } | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    const old = areaPopupRef.current;
    if (old) { old.popup.off("close", old.onClose); old.popup.remove(); areaPopupRef.current = null; }
    if (!map || !outlookArea?.ring || mode !== "live") return;
    const bounds = unwrappedBounds(outlookArea.ring);
    if (!bounds) return;
    // room above the area for the popup, and clear of the overlay card on the left
    const pad = layout === "phone"
      ? fitPad(map, 0, { top: 290 }) as { top: number; bottom: number; left: number; right: number }
      : fitPadding(map, { top: 250, bottom: 100, left: 340, right: 90 });
    map.fitBounds(bounds, { padding: pad, maxZoom: 6, duration: 800 });
    // the area lands centred between the paddings: the popup over it is no wider than fits there
    const w = map.getContainer().clientWidth, cx = pad.left + (w - pad.left - pad.right) / 2;
    const popupW = Math.round(Math.max(220, Math.min(310, 2 * Math.min(cx, w - cx) - 12)));
    const st = useStore.getState();
    const invest = outlookArea.invest_id && st.live.indexKind === "active"
      ? st.live.index.find((r) => r.storm_id === outlookArea.invest_id) ?? null : null;
    const el = areaPopupEl(outlookArea, invest);
    el.querySelector("[data-act=run]")?.addEventListener("click", () => useStore.getState().runOutlookFuturecast());
    el.querySelector("[data-act=open]")?.addEventListener("click", () => {
      if (invest) void useStore.getState().selectLiveStorm(invest.storm_id);
    });
    const onClose = () => { areaPopupRef.current = null; useStore.getState().closeOutlookArea(); };
    const popup = new maplibregl.Popup({
      closeButton: true, closeOnClick: false, className: "area-popup", maxWidth: `${popupW}px`, anchor: "bottom", offset: 10,
    }).setLngLat([(bounds[0][0] + bounds[1][0]) / 2, bounds[1][1]]).setDOMContent(el).addTo(map);
    popup.on("close", onClose);
    areaPopupRef.current = { popup, onClose };
  }, [outlookArea, mode]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => () => { areaPopupRef.current?.popup.remove(); }, []);
  useEffect(() => { engineRef.current?.setOpacity(overlays.opacity); }, [overlays.opacity, mapLoaded]);
  // coastlines and borders over the imagery while any covers the map (lightning is just dots)
  const imageryOn = !!(satSet || satBaseSet || radarSet || oceanSet || mesoSet || futureData || surgeSet);
  useEffect(() => {
    const map = mapRef.current;
    if (map && mapLoaded) showReferenceLines(map, imageryOn);
  }, [imageryOn, mapLoaded]);
  useEffect(() => { engineRef.current?.setSpeed(overlays.speed); }, [overlays.speed, mapLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (drawMode) map.dragPan.disable();
    else map.dragPan.enable();
  }, [drawMode]);

  // auto-fit to a live storm when it is opened — once per storm: the 10-min
  // background refresh swaps in a new bundle and must not yank the camera
  const lastLiveFitRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "live" || !bundle) { lastLiveFitRef.current = null; return; }
    if (lastLiveFitRef.current === bundle.storm_id) return;
    lastLiveFitRef.current = bundle.storm_id;
    const pts: [number, number][] = [
      ...bundle.best_track.map((p) => [p.lon, p.lat] as [number, number]),
      ...bundle.official_forecast.map((p) => [p.lon, p.lat] as [number, number]),
    ];
    const bounds = unwrappedBounds(pts);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: fitPad(map, 60), maxZoom: 6, duration: 800 });
  }, [mode, bundle]);

  // back to the both-basins overview: on "← All storms", or arriving on the
  // live tab with nothing open (first load is framed by the Map constructor)
  const prevLiveRef = useRef({ mode, selectedLiveId });
  useEffect(() => {
    const prev = prevLiveRef.current;
    prevLiveRef.current = { mode, selectedLiveId };
    const map = mapRef.current;
    if (!map || mode !== "live" || selectedLiveId || useStore.getState().live.futureArea) return;
    if (prev.mode !== "live" || prev.selectedLiveId) {
      map.fitBounds(overviewBounds(), { padding: fitPad(map, OVERVIEW_PADDING), duration: 800 });
    }
  }, [mode, selectedLiveId]);

  // Build C: when a compare storm loads, frame BOTH it and the live storm so the
  // contrast is immediately visible (fits once per compare storm).
  const lastCompareFitRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "live" || !compare.stormId) { lastCompareFitRef.current = null; return; }
    const cpts =
      compare.source === "replay" && compare.replay
        ? compare.replay.frames.map((f) => [f.lon, f.lat] as [number, number])
        : compare.source === "track" && compare.track
          ? compare.track.track.map((p) => [p.lon, p.lat] as [number, number])
          : [];
    if (cpts.length < 2) return; // wait until geometry loads
    if (lastCompareFitRef.current === compare.stormId) return;
    lastCompareFitRef.current = compare.stormId;
    const livePts: [number, number][] = bundle
      ? [
          ...bundle.best_track.map((p) => [p.lon, p.lat] as [number, number]),
          ...bundle.official_forecast.map((p) => [p.lon, p.lat] as [number, number]),
        ]
      : [];
    const bounds = unwrappedBounds([...cpts, ...livePts]);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: fitPad(map, 70), maxZoom: 6, duration: 800 });
  }, [mode, compare.stormId, compare.source, compare.replay, compare.track, bundle]);

  // auto-fit to the full storm track when a replay loads
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "explorer" || !replay || replay.frames.length < 2) return;
    const bounds = unwrappedBounds(replay.frames.map((f) => [f.lon, f.lat] as [number, number]));
    if (!bounds) return;
    map.fitBounds(bounds, { padding: fitPad(map, 70), maxZoom: 6, duration: 800 });
    // refit only when the storm changes, not on every hour tick
  }, [mode, replay]);

  // Feature B: auto-fit to the live overlay storm (once per storm) so the
  // spaghetti + drawn boxes are immediately usable instead of full-basin.
  const lastOverlayFitRef = useRef<string | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || mode !== "climatology" || !climoOverlay || !bundle) return;
    if (lastOverlayFitRef.current === bundle.storm_id) return;
    lastOverlayFitRef.current = bundle.storm_id;
    // Frame the decision region: the forecast cone + recent track, not the whole
    // life-cycle (which can span the basin and stay zoomed out).
    const recent = bundle.best_track.slice(-10);
    const pts: [number, number][] = [
      ...recent.map((p) => [p.lon, p.lat] as [number, number]),
      ...bundle.official_forecast.map((p) => [p.lon, p.lat] as [number, number]),
    ];
    const bounds = unwrappedBounds(pts);
    if (!bounds) return;
    map.fitBounds(bounds, { padding: fitPad(map, 80), maxZoom: 6, duration: 800 });
  }, [mode, climoOverlay, bundle]);

  // Phase C: on focus, fit the camera to the focused storm; on dismiss, restore
  // the pre-focus camera so you land back where you were.
  const lastFocusFitRef = useRef<string | null>(null);
  const preFocusCamRef = useRef<{ center: maplibregl.LngLat; zoom: number } | null>(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (focusStormId) {
      if (preFocusCamRef.current === null) {
        preFocusCamRef.current = { center: map.getCenter(), zoom: map.getZoom() };
      }
      if (lastFocusFitRef.current === focusStormId) return;
      let coords: [number, number][] = [];
      if (focusSource === "replay" && focusReplay) coords = focusReplay.frames.map((f) => [f.lon, f.lat]);
      else if (focusSource === "track" && focusTrack) coords = focusTrack.track.map((p) => [p.lon, p.lat]);
      if (coords.length < 2) return; // wait until replay/track is loaded
      lastFocusFitRef.current = focusStormId;
      const bounds = unwrappedBounds(coords);
      if (!bounds) return;
      map.fitBounds(bounds, { padding: fitPad(map, 90), maxZoom: 7, duration: 700 });
    } else {
      lastFocusFitRef.current = null;
      if (preFocusCamRef.current) {
        map.easeTo({ center: preFocusCamRef.current.center, zoom: preFocusCamRef.current.zoom, duration: 600 });
        preFocusCamRef.current = null;
      }
    }
  }, [focusStormId, focusSource, focusReplay, focusTrack]);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      <div ref={containerRef} style={{ position: "absolute", inset: 0 }} />
      {/* top-left stack: overlay card (mode-agnostic — a layer enabled in one
          mode can always be turned off in another), footprint toggles, draw box.
          A flex column, so nothing hard-codes the card's (variable) height. */}
      <div className="map-stack-tl">
        <OverlayControl
          compact={layout !== "desktop"}
          overlays={overlays}
          radar={radarSet}
          sat={satSet}
          satBase={satBaseSet}
          ocean={oceanSet}
          lightning={lightningSet}
          meso={mesoSet}
          mesoNote={mesoNote}
          mesoPossible={mode === "live" && !!stormPos}
          futurePossible={mode === "live" && (!!stormPos || (!selectedLiveId && !!futureArea))}
          futureArea={!selectedLiveId && futureArea ? {
            title: futureArea.title || "NHC outlook area", prob7: futureArea.prob7,
            color: nhcAreaColor(futureArea.prob7, futureArea.risk7),
          } : null}
          onCloseFutureArea={() => { if (overlays.future) toggleFuture(); }}
          future={futureData?.set ?? null}
          futureNote={futureNote}
          futureNoteText={futureData ? futureInfo?.note ?? null : null}
          toggleFuture={toggleFuture}
          setFutureProduct={setFutureProduct}
          setFutureModel={setFutureModel}
          setFutureChanceIn={setFutureChanceIn}
          status={engineStatus}
          archive={archiveRadar}
          setSatellite={setSatellite}
          setOcean={setOcean}
          toggleLightning={toggleLightning}
          toggleMeso={toggleMeso}
          toggleRadar={toggleRadar}
          setOpacity={setOverlayOpacity}
          toggleMotion={toggleMotion}
          setSpeed={setOverlaySpeed}
          setLoopMin={setLoopMin}
          onTogglePlay={() => engineRef.current?.togglePlay()}
          onStep={(d) => engineRef.current?.step(d)}
          onSeek={(i) => engineRef.current?.seek(i)}
        />
        {/* footprint toggles: all four on a replay of a past storm; a live storm
            has its wind field only (and a phone's Futurecast badge needs the room) */}
        {(replaying || (mode === "live" && !!bundle?.threat)) && !(layout === "phone" && overlays.future) && (
          <div className="foot-toggles" style={{ display: "flex", gap: 6 }}>
            {FOOT_TOGGLES.filter((t) => replaying || t.key === "wind").map((t) => (
              <button
                key={t.key}
                onClick={() => toggleFoot(t.key)}
                title={t.title}
                style={{
                  padding: "7px 11px", borderRadius: 8,
                  border: footLayers[t.key] ? `1px solid ${t.color}` : "1px solid rgba(255,255,255,0.15)",
                  background: footLayers[t.key] ? `${t.color}33` : "rgba(17,24,39,0.85)",
                  color: "white", font: "500 12px Inter, sans-serif", cursor: "pointer",
                }}
              >
                {t.label}
              </button>
            ))}
          </div>
        )}
        {mode === "climatology" && layout !== "phone" && (
          <button
            onClick={() => setDrawMode((d) => !d)}
            style={{
              padding: "8px 12px", borderRadius: 8,
              border: drawMode ? "1px solid #60A5FA" : "1px solid rgba(255,255,255,0.15)",
              background: drawMode ? "rgba(96,165,250,0.25)" : "rgba(17,24,39,0.85)",
              color: "white", font: "500 13px Inter, sans-serif", cursor: "pointer",
            }}
          >
            {drawMode ? "✏️ Drawing… drag a box" : "▭ Draw box"}
          </button>
        )}
      </div>
      {futureData && futureStep && futureInfo && (futureInfo.official ? (
        <div className="future-badge official">
          <b>📋 OFFICIAL</b> · NOAA WPC rainfall forecast · {futureStep.label ?? `+${futureStep.fhr} h`} total
          {" · "}through{" "}
          {new Date(futureStep.time).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZoneName: "short" })}
          <span className="sub">
            issued {new Date(futureInfo.cycle).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZoneName: "short" })}
            {" · "}Weather Prediction Center · US only
          </span>
        </div>
      ) : futureInfo.kind === "blend" ? (
        <div className="future-badge blend">
          <b>📊 BLEND</b> · NOAA NBM {String(new Date(futureInfo.cycle).getUTCHours()).padStart(2, "0")}Z
          {futureInfo.product === "chance"
            ? <> · chance of more than {futureInfo.thresholdIn}″ · {futureStep.label ?? `+${futureStep.fhr} h`}</>
            : <> · +{futureStep.fhr} h · valid{" "}
              {new Date(futureStep.time).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZoneName: "short" })}</>}
          <span className="sub">
            {futureInfo.product === "chance" ? "the chance for that period, not a total · " : "rain from now to this step · "}
            National Blend of Models (~30 models, calibrated) · US only
          </span>
        </div>
      ) : (
        <div className="future-badge">
          <b>🔮 SIMULATED</b> · {futureInfo.label} {String(new Date(futureInfo.cycle).getUTCHours()).padStart(2, "0")}Z run
          {" · "}+{futureStep.fhr} h · valid{" "}
          {new Date(futureStep.time).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZoneName: "short" })}
          <span className="sub">
            {!selectedLiveId && futureArea && (
              <>NHC outlook area: {futureArea.title || "unnamed"}
                {futureArea.prob7 != null ? ` (${futureArea.prob7}% in 7 days)` : ""} · </>
            )}
            {futureInfo.product === "rain" && <>rain from now to this step · </>}
            {futureStep.remnant
              ? `${bundle?.name ? titleCaseName(bundle.name) + "'s" : "the storm's"} remnants in the model — rain continues · `
              : ""}
            one model's forecast · not observed
          </span>
        </div>
      ))}
      {mode === "climatology" && climoOverlay && bundle && layout !== "phone" && (
        <div style={{
          position: "absolute", bottom: 12, left: 12, zIndex: 10,
          background: "rgba(17,24,39,0.85)", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8, padding: "8px 12px", font: "400 11px Inter, sans-serif", color: "rgba(255,255,255,0.8)",
        }}>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Gateway · {bundle.name}</div>
          <div><span style={{ color: "#60A5FA" }}>▬</span> Live models &nbsp; <span style={{ color: "#fff" }}>▬</span> NHC track</div>
          <div><span style={{ color: "#F472B6" }}>▬</span> Historical A→B &nbsp; <span style={{ color: "#60A5FA" }}>▭</span> Boxes</div>
        </div>
      )}
      {((mode === "explorer" && replay) || (focusSource === "replay" && focusReplay)) && layout !== "phone" && (
        <div style={{
          position: "absolute", top: 12, right: 12, zIndex: 10, maxWidth: 188,
          background: "rgba(17,24,39,0.88)", border: "1px solid rgba(255,255,255,0.1)",
          borderRadius: 8, padding: "8px 10px", font: "400 11px Inter, sans-serif", color: "rgba(255,255,255,0.8)",
        }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.5, color: "rgba(255,255,255,0.5)", marginBottom: 5 }}>
            THREAT FOOTPRINTS
          </div>
          {footLayers.wind && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: "#F1C40F" }}>●</span>
              <span style={{ color: "#F97316" }}>●</span>
              <span style={{ color: "#EF4444" }}>●</span> Wind&nbsp;— 34 / 50 / 64 kt radii
            </div>
          )}
          {footLayers.surge && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: "#38BDF8" }}>■</span>
              <span style={{ color: "#FACC15" }}>■</span>
              <span style={{ color: "#F97316" }}>■</span>
              <span style={{ color: "#DC2626" }}>■</span> Surge&nbsp;— minimal → catastrophic
              <div style={{ color: "rgba(255,255,255,0.45)", fontSize: 10, marginTop: 2 }}>
                qualitative tiers, biased to over-warn — not a height forecast
              </div>
            </div>
          )}
          {footLayers.tornado && (
            <div style={{ marginBottom: 4 }}>
              <span style={{ color: "#FF4444" }}>◣</span> Tornado&nbsp;— right-front
            </div>
          )}
          {footLayers.rain && (
            <div>
              <span style={{ color: "#22DD66" }}>▬</span> Rain&nbsp;— inland flooding
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const FOOT_TOGGLES: { key: FootLayer; label: string; color: string; title: string }[] = [
  { key: "wind", label: "💨 Wind", color: "#F59E0B", title: "Wind field (34/50/64 kt radii)" },
  { key: "surge", label: "🌊 Surge", color: "#38BDF8", title: "Coastal storm-surge threat tier" },
  { key: "tornado", label: "⚡ Tornado", color: "#EF4444", title: "Far-field tornado threat (right-front quadrant)" },
  { key: "rain", label: "🌧️ Rain", color: "#22DD66", title: "Inland flooding footprint (terrain-enhanced)" },
];

// ── explorer layer builder ──
// Saffir-Simpson colour from vmax in KNOTS (ATCF/HURDAT2 unit). Cat 5 is a
// light violet — the old dark red (139,0,0) vanished on the dark basemap.
function catColor(vmax: number): [number, number, number] {
  if (vmax >= 137) return [192, 132, 252];
  if (vmax >= 113) return [231, 76, 60];
  if (vmax >= 96) return [230, 126, 34];
  if (vmax >= 83) return [243, 156, 18];
  if (vmax >= 64) return [241, 196, 15];
  if (vmax >= 34) return [46, 204, 113];
  return [149, 165, 166];
}

function tornadoStyle(tier: number): [[number, number, number], number] {
  if (tier >= 3) return [[255, 0, 0], 0.35];
  if (tier >= 2) return [[255, 68, 68], 0.25];
  return [[255, 136, 0], 0.15];
}
function floodStyle(tier: number): [[number, number, number], number] {
  if (tier >= 3) return [[0, 204, 68], 0.2];
  if (tier >= 2) return [[34, 221, 102], 0.14];
  return [[68, 238, 136], 0.08];
}
// Surge threat tier → ribbon styling. Index by tier (0 Minimal … 3 Catastrophic).
// Width is the full ribbon width in METERS (so it grows realistically on zoom);
// higher tier reads bolder + wider. The ribbon is centered on the coastline so it
// covers the shore, and escalates into bays via the per-point tiers.
const SURGE_TIER_LABEL = ["Minimal", "Impactful", "High", "Catastrophic"];
const SURGE_TIER_HEX = ["#38BDF8", "#FACC15", "#F97316", "#DC2626"];
const SURGE_TIER_RGB: [number, number, number][] = [
  [56, 189, 248], [250, 204, 21], [249, 115, 22], [220, 38, 38],
];
const SURGE_TIER_WIDTH = [3500, 6500, 10500, 15000]; // meters (full width)
const SURGE_TIER_ALPHA = [150, 190, 218, 238];

// ── threat footprints (wind/surge/tornado/rain) — ONE builder for replay
// frames AND the live storm (backend live_threat emits replay-identical
// shapes). `prefix` keeps deck layer ids unique when both render at once. ──
interface FootPos {
  lat: number; lon: number;
  r34?: number[] | null; r50?: number[] | null; r64?: number[] | null;
}
interface FootParams {
  mb?: number | null;
  torn?: { tier: number; r34r: number };
  flood?: { tier: number; radii: number[] };
  surge?: [number, number][];
}

function buildFootprintLayers(
  cur: FootPos,
  foot: FootParams | undefined | null,
  footLayers: Record<FootLayer, boolean>,
  coastline: CoastlineDense | null,
  prefix: string,
) {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fp: any[] = [];

  // rain / inland flooding (broad, drawn first/under)
  if (footLayers.rain && foot?.flood) {
    const ring = windRadiiPolygon(cur.lat, cur.lon, foot.flood.radii);
    if (ring) {
      const [c, op] = floodStyle(foot.flood.tier);
      fp.push(new PolygonLayer({
        id: `${prefix}-flood`, data: [{ polygon: ring }],
        getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
        getFillColor: [...c, Math.round(op * 255)] as [number, number, number, number],
        getLineColor: [...c, 200] as [number, number, number, number],
        getLineWidth: 1, lineWidthUnits: "pixels", stroked: true, filled: true,
      }));
    }
  }

  // wind field
  if (footLayers.wind) {
    const bands: { radii: number[] | null | undefined; color: [number, number, number]; id: string }[] = [
      { radii: cur.r34, color: [241, 196, 15], id: "w34" },
      { radii: cur.r50, color: [249, 115, 22], id: "w50" },
      { radii: cur.r64, color: [239, 68, 68], id: "w64" },
    ];
    for (const band of bands) {
      const ring = windRadiiPolygon(cur.lat, cur.lon, band.radii ?? null);
      if (!ring) continue;
      fp.push(new PolygonLayer({
        id: `${prefix}-${band.id}`, data: [{ polygon: ring }],
        getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
        getFillColor: [...band.color, 45] as [number, number, number, number],
        getLineColor: [...band.color, 180] as [number, number, number, number],
        getLineWidth: 1, lineWidthUnits: "pixels", stroked: true, filled: true,
      }));
    }
  }

  // surge — a CONTINUOUS coastal ribbon colored by per-point threat tier. Reads
  // the [pointIdx, tier] pairs against the static dense coastline and draws
  // short segments between consecutive active points (within one arc), so the
  // band hugs the shore without breaks and escalates into bays. Pickable.
  if (footLayers.surge && foot?.surge && coastline) {
    const tierAt = new Map<number, number>();
    for (const [idx, tier] of foot.surge) tierAt.set(idx, tier);
    const pts = coastline.points; // [lat, lon]
    type RibbonSeg = {
      path: [number, number][]; tier: number;
      surgeName: string; surgeLabel: string; surgeColor: string;
    };
    const segs: RibbonSeg[] = [];
    const P = (i: number): [number, number] => [pts[i][1], pts[i][0]];
    // Walk SEGMENTS (i → i+1); each takes the higher tier of its two endpoints so
    // an isolated peak (e.g. a single Catastrophic point at a bay head) still gets
    // its colour. Group consecutive same-tier segments into smooth runs, sharing
    // the boundary point so the ribbon is gapless. A gap = unthreatened coast.
    for (const [s, e] of coastline.arcs) {
      let path: [number, number][] = [];
      let tier = -1;
      let nameIdx = -1;
      const flush = () => {
        if (path.length >= 2) {
          segs.push({
            path, tier,
            surgeName: coastline.names[nameIdx] || "Coast",
            surgeLabel: SURGE_TIER_LABEL[tier],
            surgeColor: SURGE_TIER_HEX[tier],
          });
        }
        path = [];
        tier = -1;
      };
      for (let i = s; i < e - 1; i++) {
        const t0 = tierAt.get(i);
        const t1 = tierAt.get(i + 1);
        if (t0 === undefined || t1 === undefined) { flush(); continue; }
        const segTier = Math.max(t0, t1);
        if (tier === -1) { tier = segTier; nameIdx = i; path = [P(i), P(i + 1)]; }
        else if (segTier === tier) { path.push(P(i + 1)); }
        else { flush(); tier = segTier; nameIdx = i; path = [P(i), P(i + 1)]; }
      }
      flush();
    }
    if (segs.length) {
      fp.push(new PathLayer({
        id: `${prefix}-surge`, data: segs,
        getPath: (d: RibbonSeg) => d.path,
        getColor: (d: RibbonSeg) =>
          [...SURGE_TIER_RGB[d.tier], SURGE_TIER_ALPHA[d.tier]] as [number, number, number, number],
        getWidth: (d: RibbonSeg) => SURGE_TIER_WIDTH[d.tier],
        widthUnits: "meters", widthMinPixels: 2, widthMaxPixels: 15,
        capRounded: true, jointRounded: true, pickable: true,
      }));
    }
  }

  // tornado (right-front sector)
  if (footLayers.tornado && foot?.torn && foot.mb != null) {
    const ring = sectorPolygon(cur.lat, cur.lon, foot.torn.r34r, foot.mb - 45, foot.mb + 90);
    const [c, op] = tornadoStyle(foot.torn.tier);
    fp.push(new PolygonLayer({
      id: `${prefix}-torn`, data: [{ polygon: ring }],
      getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
      getFillColor: [...c, Math.round(op * 255)] as [number, number, number, number],
      getLineColor: [...c, 220] as [number, number, number, number],
      getLineWidth: 1.5, lineWidthUnits: "pixels", stroked: true, filled: true,
    }));
  }

  return fp;
}

function buildExplorerLayers(
  replay: ReturnType<typeof useStore.getState>["explorer"]["replay"],
  hour: number,
  footLayers: Record<FootLayer, boolean>,
  coastline: CoastlineDense | null,
) {
  if (!replay || replay.frames.length === 0) return [];
  const fr = replay.frames;

  type Seg = { path: [number, number][]; color: [number, number, number] };
  const traveled: Seg[] = [];
  const ahead: Seg[] = [];
  for (let i = 0; i < fr.length - 1; i++) {
    const seg: [number, number][] = [
      [fr[i].lon, fr[i].lat],
      [fr[i + 1].lon, fr[i + 1].lat],
    ];
    if (i < hour) traveled.push({ path: seg, color: catColor(fr[i].vmax) });
    else ahead.push({ path: seg, color: [148, 163, 184] });
  }
  const cur = fr[Math.min(hour, fr.length - 1)];

  // footprints (drawn under the track) — shared with the LIVE storm
  const fp = buildFootprintLayers(cur, cur.foot, footLayers, coastline, "ex");

  return [
    ...fp,
    new PathLayer({
      id: "ex-ahead", data: ahead,
      getPath: (d: Seg) => d.path,
      getColor: [148, 163, 184, 70], getWidth: 1.5, widthUnits: "pixels", capRounded: true,
    }),
    new PathLayer({
      id: "ex-traveled", data: traveled,
      getPath: (d: Seg) => d.path,
      getColor: (d: Seg) => [...d.color, 230] as [number, number, number, number],
      getWidth: 3, widthUnits: "pixels", widthMinPixels: 2, capRounded: true, jointRounded: true,
    }),
    new ScatterplotLayer({
      id: "ex-current", data: [cur],
      getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
      getRadius: 8, radiusUnits: "pixels",
      getFillColor: [...catColor(cur.vmax), 255] as [number, number, number, number],
      stroked: true, getLineColor: [255, 255, 255, 230], lineWidthMinPixels: 2,
    }),
  ];
}

// ── Feature A: trajectory-analog boxes + downstream heatmap/lines ──
function buildAnalogLayers(analog: AnalogResult, opacity = 1) {
  const lastBox = analog.boxes.length - 1;
  const boxData = analog.boxes.map((b, i) => ({
    polygon: boxRing(b),
    // genesis box (A) teal; current box (last) white; mid box grey-blue
    color: !analog.genesisMode
      ? ([255, 255, 255] as [number, number, number])
      : i === 0
        ? ([45, 212, 191] as [number, number, number])
        : i === lastBox
          ? ([255, 255, 255] as [number, number, number])
          : ([148, 163, 184] as [number, number, number]),
  }));

  return [
    // density of where analogs went next
    new HeatmapLayer({
      id: "analog-heat",
      data: analog.downstreamPoints,
      getPosition: (d: { lon: number; lat: number }) => [d.lon, d.lat],
      getWeight: 1,
      radiusPixels: 26,
      intensity: 1,
      threshold: 0.05,
      opacity: 0.45 * opacity,
    }),
    // individual downstream paths — hoverable to identify each analog storm.
    // Capped (most recent first) so the map stays legible when many analogs match.
    new PathLayer({
      id: "analog-paths",
      data: analog.downstreamPaths.slice(0, 60),
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [125, 211, 252, 90],
      getWidth: 1.4, widthUnits: "pixels", widthMinPixels: 1, capRounded: true,
      opacity,
      pickable: true,
      autoHighlight: true,
      highlightColor: [255, 255, 255, 230],
    }),
    new PolygonLayer({
      id: "analog-boxes",
      data: boxData,
      getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
      getFillColor: (d: { color: [number, number, number] }) => [...d.color, 14] as [number, number, number, number],
      getLineColor: (d: { color: [number, number, number] }) => [...d.color, 220] as [number, number, number, number],
      getLineWidth: 1.5, lineWidthUnits: "pixels", stroked: true, filled: true, opacity,
    }),
  ];
}

// ── lat/lon graticule (MapLibre base layers, present in every mode) ──
function addGraticule(map: maplibregl.Map) {
  const feats: GeoJSON.Feature[] = [];
  const mk = (coords: [number, number][], label: string) =>
    feats.push({ type: "Feature", properties: { label }, geometry: { type: "LineString", coordinates: coords } });
  for (let lon = -180; lon <= 180; lon += 10) {
    const pts: [number, number][] = [];
    for (let lat = -80; lat <= 80; lat += 2) pts.push([lon, lat]);
    mk(pts, `${Math.abs(lon)}°${lon < 0 ? "W" : lon > 0 ? "E" : ""}`);
  }
  for (let lat = -80; lat <= 80; lat += 10) {
    const pts: [number, number][] = [];
    for (let lon = -180; lon <= 180; lon += 2) pts.push([lon, lat]);
    mk(pts, `${Math.abs(lat)}°${lat < 0 ? "S" : lat > 0 ? "N" : ""}`);
  }
  map.addSource("graticule", { type: "geojson", data: { type: "FeatureCollection", features: feats } });
  map.addLayer({
    id: "graticule-lines", type: "line", source: "graticule",
    paint: { "line-color": "#9fb3c8", "line-opacity": 0.12, "line-width": 0.6 },
  });
  map.addLayer({
    id: "graticule-labels", type: "symbol", source: "graticule",
    layout: {
      "symbol-placement": "line", "text-field": ["get", "label"],
      "text-font": ["Open Sans Regular"], "text-size": 10, "symbol-spacing": 260,
    },
    paint: {
      "text-color": "#9fb3c8", "text-opacity": 0.45,
      "text-halo-color": "#0a0e17", "text-halo-width": 1.2,
    },
  });
}

// ── NHC GTWO: official colour-coded genesis areas + ✕ disturbance markers ──
// (markers an invest's own symbol already stands for are left out). A click
// on an area or its ✕ opens it: NHC's odds and words, and a futurecast of
// what the models do with it. `openId`: the area open now (drawn bolder).
function buildGtwoLayers(gtwo: GtwoFeature[] | undefined, hidePoints?: Set<GtwoFeature>, openId: string | null = null) {
  if (!gtwo || gtwo.length === 0) return [];
  const areas = gtwo.filter((f) => f.kind === "area" && f.ring && f.ring.length >= 3);
  const points = gtwo.filter((f) => f.kind === "point" && f.lon != null && f.lat != null && !hidePoints?.has(f));
  const isOpen = (d: GtwoFeature) => !!openId && d.id === openId;
  const open = (info: { object?: GtwoFeature | null }) => openOutlookFeature(info.object ?? null, gtwo);
  return [
    new PolygonLayer({
      id: "nhc-gtwo-areas", data: areas,
      getPolygon: (d: GtwoFeature) => d.ring as [number, number][],
      getFillColor: (d: GtwoFeature) => [...hexToRgb(nhcAreaColor(d.prob7, d.risk7)), isOpen(d) ? 70 : 45] as [number, number, number, number],
      getLineColor: (d: GtwoFeature) => [...hexToRgb(nhcAreaColor(d.prob7, d.risk7)), 230] as [number, number, number, number],
      getLineWidth: (d: GtwoFeature) => (isOpen(d) ? 3.5 : 1.8), lineWidthUnits: "pixels", stroked: true, filled: true,
      pickable: true, onClick: open,
      updateTriggers: { getFillColor: openId, getLineWidth: openId },
    }),
    new TextLayer({
      id: "nhc-gtwo-x", data: points,
      getPosition: (d: GtwoFeature) => [d.lon as number, d.lat as number],
      getText: () => "✕", getSize: 17, sizeUnits: "pixels",
      getColor: (d: GtwoFeature) => [...hexToRgb(nhcAreaColor(d.prob7, d.risk7)), 255] as [number, number, number, number],
      fontWeight: 700, pickable: true, onClick: open,
    }),
  ];
}

/** A click on an outlook area or its ✕ opens that area. Not in the rain view:
 *  a tap there is the rain total at that spot, areas included. */
function openOutlookFeature(f: GtwoFeature | null, gtwo: GtwoFeature[]): boolean {
  const st = useStore.getState();
  const rainView = st.overlays.futureProduct === "rain" || st.overlays.futureProduct === "chance";
  if (!f || (st.overlays.future && rainView && st.live.futureInfo)) return false;
  const area = f.kind === "area" ? f
    : gtwo.find((a) => a.kind === "area" && !!a.id && a.id === f.id)
      ?? gtwo.find((a) => a.kind === "area" && !!a.ring && f.lon != null && f.lat != null && inRing(f.lon, f.lat, a.ring));
  if (!area?.id) return false;
  st.openOutlookArea(area);
  return true;
}

type Pad = { top: number; bottom: number; left: number; right: number };

/** Padding for a camera fit. Desktop: `desktop`, as it always was. Tablet:
 *  the same, scaled to the narrower map. Phone: what the ☰ row (plus
 *  `extra.top`) and the bottom sheet (plus `extra.bottom`) cover. */
function fitPad(map: maplibregl.Map, desktop: number | Pad, extra: { top?: number; bottom?: number } = {}): number | Pad {
  const ui = useStore.getState().ui;
  if (ui.layout === "desktop") return desktop;
  if (ui.layout === "tablet") {
    return fitPadding(map, typeof desktop === "number" ? { top: desktop, bottom: desktop, left: desktop, right: desktop } : desktop);
  }
  const h = map.getContainer().clientHeight;
  return {
    top: 64 + (extra.top ?? 0), left: 16, right: 16,
    bottom: Math.min(h * 0.75, sheetHeight(ui.sheet, h) + 24 + (extra.bottom ?? 0)),
  };
}

/** Map padding scaled down on a small screen, so the content keeps >=40% of it. */
function fitPadding(map: maplibregl.Map, p: { top: number; bottom: number; left: number; right: number }) {
  const { clientWidth: w, clientHeight: h } = map.getContainer();
  const kx = Math.min(1, (0.6 * w) / (p.left + p.right)), ky = Math.min(1, (0.6 * h) / (p.top + p.bottom));
  return { top: p.top * ky, bottom: p.bottom * ky, left: p.left * kx, right: p.right * kx };
}

const esc = (s: string) => s.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
const BASIN_NAME: Record<string, string> = { AL: "Atlantic", EP: "Eastern Pacific", CP: "Central Pacific" };

/** The popup of a clicked outlook area: NHC's odds and discussion, and the
 *  way into its futurecast (its invest's, once NHC has made it one). */
function areaPopupEl(area: GtwoFeature, invest: StormRef | null): HTMLElement {
  const chip = (p: number | null, risk: string | null | undefined, span: string) =>
    `<span class="ap-chip" style="--c:${nhcAreaColor(p, risk ?? null)}"><b>${p == null ? "?" : `${p}%`}</b> ${span}</span>`;
  const inv = invest ? esc(systemName(invest)) : null;
  const el = document.createElement("div");
  el.className = "ap";
  el.innerHTML = `
    <div class="ap-kicker">NHC outlook area · ${esc(BASIN_NAME[area.basin] ?? area.basin)}</div>
    <div class="ap-title">${esc(area.title || "Area of interest")}</div>
    <div class="ap-odds">${chip(area.prob2, area.risk2, "in 48 h")}${chip(area.prob7, area.risk7, "in 7 days")}</div>
    ${area.discussion ? `<div class="ap-text">${esc(area.discussion)}</div>` : ""}
    <button class="ap-run" data-act="run">🔮 Futurecast: what the models do with it</button>
    <div class="ap-sub">${inv
      ? `NHC is tracking it as <b>${inv}</b>: this opens it with its futurecast`
      : "ECMWF and GFS: simulated clouds, radar and rain for the next 6 days. Model scenarios, not observations"}</div>
    ${inv ? `<button class="ap-link" data-act="open">Open ${inv} ›</button>` : ""}`;
  return el;
}

// ── genesis-watch markers: a support-coloured dot per disturbance ──
// Off by default (model signals are busy on a first look); a row picked in
// the Genesis Watch list still marks its own disturbance.
function buildGenesisLayers(genesis: GenesisWatch | null, selectedIdx: number | null, showAll: boolean) {
  if (!genesis || genesis.watch.length === 0) return [];
  const items = genesis.watch.map((w, i) => ({ ...w, idx: i })).filter((w) => showAll || w.idx === selectedIdx);
  if (!items.length) return [];
  return [
    new ScatterplotLayer({
      id: "genesis-dots", data: items,
      getPosition: (d: { lon: number; lat: number }) => [d.lon, d.lat],
      getRadius: (d: { idx: number }) => (d.idx === selectedIdx ? 11 : 6),
      radiusUnits: "pixels",
      getFillColor: (d: { idx: number; max_develop_pct: number }) =>
        [...hexToRgb(genesisColor(d.max_develop_pct)), d.idx === selectedIdx ? 255 : 165] as [number, number, number, number],
      stroked: true, lineWidthUnits: "pixels",
      getLineColor: (d: { idx: number }) => (d.idx === selectedIdx ? [255, 255, 255, 255] : [255, 255, 255, 90]),
      getLineWidth: (d: { idx: number }) => (d.idx === selectedIdx ? 2 : 1),
      pickable: true,
      onClick: (info: { object?: { idx: number } | null }) => {
        if (info.object) useStore.getState().selectGenesis(info.object.idx);
      },
    }),
    new TextLayer({
      id: "genesis-labels", data: items,
      getPosition: (d: { lon: number; lat: number }) => [d.lon, d.lat],
      getText: (d: { both: boolean; max_develop_pct: number }) =>
        `${d.both ? "◆ " : ""}${d.max_develop_pct}%`,
      getSize: 11, sizeUnits: "pixels",
      getColor: (d: { idx: number }) => (d.idx === selectedIdx ? [255, 255, 255, 255] : [205, 215, 230, 170]),
      getPixelOffset: [0, -14], getTextAnchor: "middle", getAlignmentBaseline: "center",
    }),
  ];
}

// ── OFFICIAL NHC products: forecast cone + coastal watch/warning segments ──
// Deliberately styled apart from the model/ensemble guidance lines: the
// cone is a neutral white official boundary; WW segments use NHC's 4-class
// colors. This is what a reader must never confuse with model guidance.
function buildOfficialLayers(
  gis: ReturnType<typeof useStore.getState>["live"]["officialGis"],
  opacity = 1,
) {
  if (!gis) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const layers: any[] = [];
  if (gis.cone && gis.cone.ring.length >= 4) {
    layers.push(
      new PolygonLayer({
        id: "official-cone", data: [{ polygon: gis.cone.ring }],
        getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
        getFillColor: [255, 255, 255, 18], getLineColor: [255, 255, 255, 200],
        getLineWidth: 2, lineWidthUnits: "pixels", stroked: true, filled: true, opacity,
      }),
    );
  }
  if (gis.ww.length) {
    layers.push(
      new PathLayer({
        id: "official-ww", data: gis.ww,
        getPath: (d: { path: [number, number][] }) => d.path,
        getColor: (d: { color: string }) => [...hexToRgb(d.color), 235] as [number, number, number, number],
        getWidth: 4.5, widthUnits: "pixels", widthMinPixels: 3, capRounded: true, jointRounded: true,
        pickable: true, opacity,
      }),
    );
  }
  return layers;
}

// ── NWS active alerts (tornado / flash-flood / hurricane warnings) ──
function buildAlertLayers(alerts: ReturnType<typeof useStore.getState>["live"]["alerts"]) {
  if (!alerts.length) return [];
  const color = (event: string): [number, number, number] =>
    /tornado/i.test(event) ? [239, 68, 68]
      : /flash flood/i.test(event) ? [34, 197, 94]
        : /surge/i.test(event) ? [168, 85, 247]
          : [244, 114, 182];
  return [
    new PolygonLayer({
      id: "nws-alerts", data: alerts,
      getPolygon: (d: { polygon: [number, number][] }) => d.polygon,
      getFillColor: (d: { event: string }) => [...color(d.event), 55] as [number, number, number, number],
      getLineColor: (d: { event: string }) => [...color(d.event), 230] as [number, number, number, number],
      getLineWidth: 1.5, lineWidthUnits: "pixels", stroked: true, filled: true, pickable: true,
    }),
  ];
}

// ── Hurricane-hunter recon: 30-s flight obs + vortex-message center fixes ──
// Dots are coloured by the SFMR SURFACE wind on the Saffir-Simpson scale;
// where the aircraft only reports flight-level wind the dot is drawn faint
// (flight-level winds run ~10 % above the surface). Fixes carry the
// aircraft-measured central pressure.
type ReconDot = {
  reconKind: "ob"; position: [number, number]; rgb: [number, number, number]; surface: boolean;
  ac: string; t: string; fl_dir: number | null; fl_kt: number | null; sfmr_kt: number | null;
  slp: number | null; t_c: number | null;
};
type ReconFixPt = {
  reconKind: "fix"; position: [number, number]; latest: boolean; label: string;
  t: string; mslp: number | null; extrap: boolean; eye: string | null;
  max_fl_kt: number | null; max_sfc_kt: number | null; ac: string | null;
};
function buildReconLayers(recon: ReturnType<typeof useStore.getState>["live"]["recon"], opacity = 1) {
  if (!recon || (!recon.flights.length && !recon.fixes.length)) return [];
  const dots: ReconDot[] = [];
  const paths: { path: [number, number][] }[] = [];
  for (const f of recon.flights) {
    const path: [number, number][] = [];
    for (const o of f.obs) {
      path.push([o.lon, o.lat]);
      const kt = o.sfmr_kt ?? o.fl_kt;
      if (kt == null) continue;
      dots.push({
        reconKind: "ob", position: [o.lon, o.lat], rgb: catColor(kt), surface: o.sfmr_kt != null,
        ac: f.aircraft, t: o.t, fl_dir: o.fl_dir, fl_kt: o.fl_kt, sfmr_kt: o.sfmr_kt, slp: o.slp, t_c: o.t_c,
      });
    }
    if (path.length >= 2) paths.push({ path });
  }
  const zt = (iso: string) => `${iso.slice(11, 16)}Z`;
  const fixes: ReconFixPt[] = recon.fixes.map((x, i) => ({
    reconKind: "fix", position: [x.lon, x.lat], latest: i === recon.fixes.length - 1,
    label: x.mslp != null ? `${x.mslp} mb · ${zt(x.t)}` : zt(x.t),
    t: x.t, mslp: x.mslp, extrap: x.extrap, eye: x.eye, max_fl_kt: x.max_fl_kt, max_sfc_kt: x.max_sfc_kt, ac: x.aircraft,
  }));
  return [
    new PathLayer({
      id: "recon-paths", data: paths,
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [226, 232, 240, 70], getWidth: 1, widthUnits: "pixels", opacity,
    }),
    new ScatterplotLayer({
      id: "recon-obs", data: dots,
      getPosition: (d: ReconDot) => d.position,
      getRadius: (d: ReconDot) => (d.surface ? 3 : 2.2), radiusUnits: "pixels",
      getFillColor: (d: ReconDot) => [...d.rgb, d.surface ? 235 : 110] as [number, number, number, number],
      pickable: true, opacity,
    }),
    new ScatterplotLayer({
      id: "recon-fixes", data: fixes,
      getPosition: (d: ReconFixPt) => d.position,
      getRadius: (d: ReconFixPt) => (d.latest ? 6.5 : 4.5), radiusUnits: "pixels",
      getFillColor: [15, 23, 42, 235], stroked: true,
      getLineColor: (d: ReconFixPt) => (d.latest ? [56, 189, 248, 255] : [148, 197, 255, 200]),
      lineWidthMinPixels: 2, pickable: true, opacity,
    }),
    new TextLayer({
      // only the newest few, or a long mission's fixes pile into one smear
      id: "recon-fix-labels", data: fixes.slice(-3),
      getPosition: (d: ReconFixPt) => d.position,
      getText: (d: ReconFixPt) => `✈ ${d.label}`,
      getColor: (d: ReconFixPt) => (d.latest ? [186, 230, 253, 255] : [186, 230, 253, 200]),
      getSize: 10.5, sizeUnits: "pixels", fontFamily: "Inter, sans-serif", fontWeight: 700,
      characterSet: "auto",
      getPixelOffset: (d: ReconFixPt) => [0, d.latest ? -16 : -13], getTextAnchor: "middle", getAlignmentBaseline: "bottom",
      background: true, getBackgroundColor: [10, 14, 23, 200], backgroundPadding: [4, 2], opacity,
    }),
  ];
}

const titleCaseName = (s: string) => s.toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());

/** The rain view's point readout: total from now to the step on screen, the
 *  run's total, and a small cumulative curve with the current step marked. */
/** "Thu 10/8 8 PM": a futurecast step's moment in the viewer's own time. */
function futureStepWhen(ms: number): string {
  const d = new Date(ms);
  const day = d.toLocaleString("en-US", { weekday: "short" });
  const hour = d.toLocaleString("en-US", { hour: "numeric" });
  return `${day} ${d.getMonth() + 1}/${d.getDate()} ${hour}`;
}

type PointSeries = { fhr: number; in?: number; pct?: number }[];

function rainPopupHtml(series: PointSeries | null, info: FutureInfo, step: FutureInfo["steps"][number]) {
  if (series == null) return `<div class="rp"><b>🌧️ Rain here</b><div class="rp-sub">loading…</div></div>`;
  const base = series.find((p) => p.fhr === info.fromFhr)?.in ?? 0;
  const pts = series.filter((p) => p.fhr >= info.fromFhr).map((p) => ({ fhr: p.fhr, v: Math.max(0, (p.in ?? 0) - base) }));
  if (!pts.length) return `<div class="rp"><b>🌧️ Rain here</b><div class="rp-sub">no data for this spot yet</div></div>`;
  const now = pts.filter((p) => p.fhr <= step.fhr).at(-1)?.v ?? 0;
  const total = pts.at(-1)!.v;
  const W = 220, H = 56, max = Math.max(0.5, total);
  const x = (fhr: number) => ((fhr - pts[0].fhr) / Math.max(1, pts.at(-1)!.fhr - pts[0].fhr)) * (W - 4) + 2;
  const y = (v: number) => H - 4 - (v / max) * (H - 10);
  const path = pts.map((p, i) => `${i ? "L" : "M"}${x(p.fhr).toFixed(1)},${y(p.v).toFixed(1)}`).join(" ");
  const cx = x(Math.max(pts[0].fhr, Math.min(step.fhr, pts.at(-1)!.fhr)));
  const valid = new Date(step.time).toLocaleString("en-US", { weekday: "short", hour: "numeric", timeZoneName: "short" });
  const f = (v: number) => (v >= 10 ? v.toFixed(0) : v.toFixed(1));
  const last = info.steps.find((s) => s.fhr === pts.at(-1)!.fhr);
  return `<div class="rp"><b>🌧️ Rain here · ${info.label}</b>
    <div class="rp-big">${f(now)} in <span>by ${valid} (${step.label ?? `+${step.fhr} h`})</span></div>
    <div class="rp-sub">${f(total)} in ${last?.label ? `over ${last.label.toLowerCase()}` : `through +${pts.at(-1)!.fhr} h`}</div>
    <svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">
      <path d="${path} L${x(pts.at(-1)!.fhr).toFixed(1)},${H - 4} L${x(pts[0].fhr).toFixed(1)},${H - 4} Z" fill="rgba(47,128,237,0.25)" />
      <path d="${path}" fill="none" stroke="#60a5fa" stroke-width="2" />
      <line x1="${cx}" x2="${cx}" y1="2" y2="${H - 2}" stroke="#f472b6" stroke-width="1.5" />
    </svg>
    <div class="rp-sub">${info.official ? "NOAA WPC official forecast"
      : info.kind === "blend" ? "NOAA's blend of ~30 models — not official" : "one model's forecast (simulated) — not official"}</div></div>`;
}

/** NBM's chance view at a spot: the chance of more than the threshold, period by period. */
function chancePopupHtml(series: PointSeries | null, info: FutureInfo, step: FutureInfo["steps"][number]) {
  const head = `<b>🎲 Chance of more than ${info.thresholdIn}″ here</b>`;
  if (series == null) return `<div class="rp">${head}<div class="rp-sub">loading…</div></div>`;
  if (!series.length) return `<div class="rp">${head}<div class="rp-sub">no data for this spot yet</div></div>`;
  const rows = info.steps.map((s) => {
    const p = series.find((x) => x.fhr === s.fhr)?.pct;
    const on = s.fhr === step.fhr;
    const w = p == null ? 0 : Math.max(2, Math.min(100, p));
    return `<div class="rp-row${on ? " on" : ""}"><span>${s.label ?? `+${s.fhr} h`}</span>`
      + `<span class="rp-bar"><i style="width:${w}%"></i></span><b>${p == null ? "…" : `${Math.round(p)}%`}</b></div>`;
  }).join("");
  return `<div class="rp">${head}${rows}<div class="rp-sub">NOAA National Blend of Models · each period on its own, not a total</div></div>`;
}

// ── futurecast: the playing model's own track, over the guidance lines ──
function buildFutureTrackLayers(info: FutureInfo, opacity = 1) {
  if (info.track.length < 2) return [];
  return [
    new PathLayer({
      id: "future-track", data: [{ path: info.track }],
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [244, 114, 182, 235], getWidth: 3.5, widthUnits: "pixels", widthMinPixels: 2,
      capRounded: true, jointRounded: true, opacity,
    }),
  ];
}

// ── run-to-run scrub: previous cycle's official forecast, ghosted ──
// Violet = the prior advisory's OFCL track; the current cycle's OFCL is white
// (from buildLiveLayers), so the two diverging is the run-to-run shift.
function buildGhostLayers(
  prev: ReturnType<typeof useStore.getState>["live"]["scrubPrevBundle"],
  opacity = 1,
) {
  if (!prev) return [];
  const path = prev.official_forecast.map((p) => [p.lon, p.lat] as [number, number]);
  if (path.length < 2) return [];
  return [
    new PathLayer({
      id: "scrub-ghost-ofcl", data: [{ path }],
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [167, 139, 250, 150], // violet = previous cycle's official track
      getWidth: 1.5, widthUnits: "pixels", widthMinPixels: 1, capRounded: true, opacity,
    }),
  ];
}

// ── Build C: a historical storm rendered beside the live storm (amber) ──
// A clean track + current marker (no footprints) so it reads as "the comparison
// storm." Driven by compare.hour, so its scrubber animates the traveled portion.
function buildCompareLayers(compare: ReturnType<typeof useStore.getState>["compare"]) {
  if (!compare.stormId) return [];
  let pts: { lon: number; lat: number }[] = [];
  if (compare.source === "replay" && compare.replay) {
    pts = compare.replay.frames.map((f) => ({ lon: f.lon, lat: f.lat }));
  } else if (compare.source === "track" && compare.track) {
    pts = compare.track.track.map((p) => ({ lon: p.lon, lat: p.lat }));
  }
  if (pts.length < 2) return [];
  const AMBER: [number, number, number] = [245, 158, 11];
  const hour = Math.min(Math.max(compare.hour, 0), pts.length - 1);
  const traveled = pts.slice(0, hour + 1).map((p) => [p.lon, p.lat] as [number, number]);
  const ahead = pts.slice(hour).map((p) => [p.lon, p.lat] as [number, number]);
  const cur = pts[hour];
  return [
    new PathLayer({
      id: "compare-ahead", data: ahead.length >= 2 ? [{ path: ahead }] : [],
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [...AMBER, 70] as [number, number, number, number],
      getWidth: 2, widthUnits: "pixels", capRounded: true,
    }),
    new PathLayer({
      id: "compare-traveled", data: traveled.length >= 2 ? [{ path: traveled }] : [],
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [...AMBER, 235] as [number, number, number, number],
      getWidth: 3, widthUnits: "pixels", widthMinPixels: 2, capRounded: true, jointRounded: true,
    }),
    new ScatterplotLayer({
      id: "compare-current", data: [cur],
      getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
      getRadius: 7, radiusUnits: "pixels",
      getFillColor: [...AMBER, 255] as [number, number, number, number],
      stroked: true, getLineColor: [255, 255, 255, 230], lineWidthMinPixels: 2,
    }),
  ];
}

// ── live layer builder ──
function buildLiveLayers(
  bundle: ReturnType<typeof useStore.getState>["live"]["bundle"],
  opacity = 1,
  opts: { models?: string[]; shownEnsembles?: string[]; ensembleAlpha?: number } = {},
) {
  if (!bundle) return [];

  // Model chips and ensemble-source chips are pure client-side visibility
  // filters: the bundle carries the full suite, so toggling never refetches
  // (and never nukes run-scrub state).
  const visibleRuns = Object.entries(bundle.model_runs)
    .filter(([tech]) => !opts.models || opts.models.includes(tech))
    .map(([, run]) => run);
  const spaghetti = visibleRuns.map((run) => ({
    path: run.points.map((p) => [p.lon, p.lat] as [number, number]),
    color: hexToRgb(run.color),
    tech: run.tech,
    techName: run.tech_name || run.tech,
  }));
  // daily time marks on the model lines: the same moments on every model
  // (the official forecast's daily times when NHC has one, else 00Z), so
  // where each model has the storm then — and how fast — reads at a glance
  const officialStart = bundle.official_init && bundle.official_forecast.length ? utcMs(bundle.official_init) : NaN;
  const now = Date.now();
  const marks = markTimes(Number.isFinite(officialStart) ? officialStart : lastMidnightUtc(now), now);
  type ModelDot = { lon: number; lat: number; dotTime: number; vmax: number | null; tech: string; techName: string; color: [number, number, number] };
  const modelDots: ModelDot[] = visibleRuns.flatMap((run) => {
    const init = run.init_time ? utcMs(String(run.init_time)) : NaN;
    if (!Number.isFinite(init) || run.points.length < 2) return [];
    return marks.flatMap((t) => {
      const at = positionAt(run.points, init, t);
      return at ? [{ ...at, dotTime: t, tech: run.tech, techName: run.tech_name || run.tech, color: hexToRgb(run.color) }] : [];
    });
  });
  // with no official forecast (an invest) the day goes beside one central line's dots
  const ref = visibleRuns.find((r) => r.tech === "TVCN") ?? visibleRuns[0];
  const dayLabels = Number.isFinite(officialStart) || !ref ? [] : modelDots
    .filter((d) => d.tech === ref.tech)
    .map((d) => ({ ...d, label: new Date(d.dotTime).toLocaleString("en-US", { weekday: "short", hour: "numeric" }) }));
  const officialPath = bundle.official_forecast.map((p) => [p.lon, p.lat] as [number, number]);
  const bestPath = bundle.best_track.map((p) => [p.lon, p.lat] as [number, number]);
  const last = bundle.best_track[bundle.best_track.length - 1];

  // observed track coloured by intensity (Saffir-Simpson) — where it
  // strengthened is readable at a glance; pre-genesis / post-tropical stages grey
  const bt = bundle.best_track;
  type TrackSeg = { path: [number, number][]; color: [number, number, number, number]; width: number };
  const trackSegs: TrackSeg[] = [];
  for (let i = 0; i < bt.length - 1; i++) {
    const tropical = !NON_TROPICAL.has((bt[i].storm_type ?? "").toUpperCase());
    trackSegs.push({
      path: [[bt[i].lon, bt[i].lat], [bt[i + 1].lon, bt[i + 1].lat]],
      color: tropical ? [...catColor(bt[i].vmax), 245] : [148, 163, 184, 190],
      width: tropical ? 3.2 : 2,
    });
  }

  // NHC-style forecast points: D/S/H/M letters on intensity colour + valid time
  const initRaw = bundle.official_init ?? bundle.latest_init ?? null;
  const initMs = initRaw ? Date.parse(/[zZ]|[+-]\d\d:?\d\d$/.test(initRaw) ? initRaw : `${initRaw}Z`) : NaN;
  const fcstPts = bundle.official_forecast
    .filter((p) => p.tau > 0 && p.tau % 12 === 0)
    .map((p) => {
      const nonTrop = NON_TROPICAL.has((p.storm_type ?? "").toUpperCase());
      const rgb = nonTrop ? ([148, 163, 184] as [number, number, number]) : catColor(p.vmax);
      return {
        lon: p.lon, lat: p.lat, rgb,
        letter: nonTrop ? "L" : p.vmax >= 96 ? "M" : p.vmax >= 64 ? "H" : p.vmax >= 34 ? "S" : "D",
        dark: rgb[0] * 0.299 + rgb[1] * 0.587 + rgb[2] * 0.114 > 140, // dark letter on bright fills
        label: Number.isFinite(initMs)
          ? new Date(initMs + p.tau * 3_600_000).toLocaleString("en-US", { weekday: "short", hour: "numeric" })
          : `+${p.tau}h`,
      };
    });

  // Ensemble member traces — one layer for every visible source (per-datum
  // colour), thin and faint so the deterministic aids stay readable on top.
  // Per-line alpha eases down with member count (fourth root) so a 1000-member
  // ensemble reads as a density cloud without losing its tails, and the
  // sidebar intensity knob scales it; the biggest sets are drawn first so the
  // smaller ones stay visible over them.
  const shown = new Set(opts.shownEnsembles ?? []);
  const ensemblePaths = ensembleSets(bundle)
    .filter((set) => shown.has(set.source))
    .sort((a, b) => b.members.length - a.members.length)
    .flatMap((set) => {
      const rgb = hexToRgb(ensembleColor(set.source));
      const n = Math.max(1, set.members.length);
      // 72 at 50 members, ~34 at 1000 (fourth root), floored so tails stay legible
      const base = Math.min(95, Math.max(30, 72 * Math.pow(50 / n, 0.25)));
      const alpha = Math.round(Math.min(230, Math.max(8, base * (opts.ensembleAlpha ?? 1))));
      return set.members.map((m) => ({
        path: m.points.map((p) => [p.lon, p.lat] as [number, number]),
        color: [rgb[0], rgb[1], rgb[2], alpha] as [number, number, number, number],
      }));
    });

  return [
    ensemblePaths.length > 0 &&
      new PathLayer({
        id: "ensemble-members", data: ensemblePaths,
        getPath: (d: { path: [number, number][] }) => d.path,
        getColor: (d: { color: [number, number, number, number] }) => d.color,
        getWidth: 1, widthUnits: "pixels",
        widthMinPixels: 1, capRounded: true, opacity,
      }),
    new PathLayer({
      id: "spaghetti", data: spaghetti,
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: (d: { color: [number, number, number] }) => [...d.color, 150] as [number, number, number, number],
      getWidth: 1.5, widthUnits: "pixels", widthMinPixels: 1, capRounded: true, opacity,
      // hover identifies the model — the sidebar chips were the only key before
      pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 200],
    }),
    new ScatterplotLayer({
      id: "model-time-dots", data: modelDots,
      getPosition: (d: ModelDot) => [d.lon, d.lat],
      getRadius: 3.5, radiusUnits: "pixels", opacity,
      getFillColor: (d: ModelDot) => [...d.color, 245] as [number, number, number, number],
      stroked: true, getLineColor: [10, 14, 23, 230], lineWidthMinPixels: 1,
      pickable: true, autoHighlight: true, highlightColor: [255, 255, 255, 230],
    }),
    dayLabels.length > 0 &&
      new TextLayer({
        id: "model-time-labels", data: dayLabels,
        getPosition: (d: ModelDot) => [d.lon, d.lat],
        getText: (d: { label: string }) => d.label,
        getColor: [235, 240, 248, 230], getSize: 10, sizeUnits: "pixels",
        fontFamily: "Inter, sans-serif", fontWeight: 600,
        getPixelOffset: [8, 0], getTextAnchor: "start", getAlignmentBaseline: "center",
        background: true, getBackgroundColor: [10, 14, 23, 185], backgroundPadding: [3, 1],
        opacity,
      }),
    // dark casing so the coloured track reads over satellite / radar imagery
    new PathLayer({
      id: "best-casing", data: bestPath.length ? [{ path: bestPath }] : [],
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [10, 14, 23, 200], getWidth: 6, widthUnits: "pixels", capRounded: true, jointRounded: true, opacity,
    }),
    new PathLayer({
      id: "best", data: trackSegs,
      getPath: (d: TrackSeg) => d.path,
      getColor: (d: TrackSeg) => d.color,
      getWidth: (d: TrackSeg) => d.width, widthUnits: "pixels", capRounded: true, jointRounded: true, opacity,
    }),
    new PathLayer({
      id: "official", data: officialPath.length ? [{ path: officialPath }] : [],
      getPath: (d: { path: [number, number][] }) => d.path,
      getColor: [255, 255, 255, 215], getWidth: 2, widthUnits: "pixels", capRounded: true, opacity,
    }),
    new ScatterplotLayer({
      id: "fcst-pts", data: fcstPts,
      getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
      getRadius: 8.5, radiusUnits: "pixels", opacity,
      getFillColor: (p: { rgb: [number, number, number] }) => [...p.rgb, 255] as [number, number, number, number],
      stroked: true, getLineColor: [10, 14, 23, 255], lineWidthMinPixels: 1.5,
    }),
    new TextLayer({
      id: "fcst-letters", data: fcstPts,
      getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
      getText: (p: { letter: string }) => p.letter,
      getColor: (p: { dark: boolean }) => (p.dark ? [17, 24, 39, 255] : [255, 255, 255, 255]),
      getSize: 10.5, sizeUnits: "pixels", fontFamily: "Inter, sans-serif", fontWeight: 800,
      getTextAnchor: "middle", getAlignmentBaseline: "center", opacity,
    }),
    new TextLayer({
      id: "fcst-labels", data: fcstPts,
      getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
      getText: (p: { label: string }) => p.label,
      getColor: [235, 240, 248, 235], getSize: 10.5, sizeUnits: "pixels",
      fontFamily: "Inter, sans-serif", fontWeight: 600,
      getPixelOffset: [13, 0], getTextAnchor: "start", getAlignmentBaseline: "center",
      background: true, getBackgroundColor: [10, 14, 23, 190], backgroundPadding: [4, 2],
      opacity,
    }),
    last &&
      new ScatterplotLayer({
        id: "current", data: [last],
        getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
        getRadius: 8, radiusUnits: "pixels", opacity,
        getFillColor: [...catColor(last.vmax), 255] as [number, number, number, number],
        stroked: true, getLineColor: [255, 255, 255, 255], lineWidthMinPixels: 2.5,
      }),
  ].filter(Boolean);
}

// ATCF stages that are not a tropical/subtropical cyclone (drawn grey)
const NON_TROPICAL = new Set(["DB", "LO", "WV", "EX", "PT", "IN", "DS", "ET", "MD", "XX"]);

// pre-2004 focus: intensity-coloured track + peak marker (no replay)
function buildTrackLayers(track: Storm) {
  const t = track.track;
  type Seg = { path: [number, number][]; color: [number, number, number] };
  const segs: Seg[] = [];
  for (let i = 0; i < t.length - 1; i++) {
    segs.push({ path: [[t[i].lon, t[i].lat], [t[i + 1].lon, t[i + 1].lat]], color: catColor(t[i].vmax) });
  }
  let peak = t[0];
  for (const p of t) if (p.vmax > peak.vmax) peak = p;
  return [
    new PathLayer({
      id: "focus-track", data: segs,
      getPath: (d: Seg) => d.path,
      getColor: (d: Seg) => [...d.color, 235] as [number, number, number, number],
      getWidth: 3, widthUnits: "pixels", widthMinPixels: 2, capRounded: true, jointRounded: true,
    }),
    new ScatterplotLayer({
      id: "focus-peak", data: [peak],
      getPosition: (p: { lon: number; lat: number }) => [p.lon, p.lat],
      getRadius: 7, radiusUnits: "pixels",
      getFillColor: [...catColor(peak.vmax), 255] as [number, number, number, number],
      stroked: true, getLineColor: [255, 255, 255, 230], lineWidthMinPixels: 2,
    }),
  ];
}
